import { Box } from '@mui/material';
import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader, type AppHeaderProps } from '@/components/layout';
import { layout } from '@/constants/layout';

interface PortalLayoutProps extends AppHeaderProps {
  sidebar: ReactNode;
  children: ReactNode;
  showSidebar?: boolean;
  sidebarWidth?: number;
}

export function PortalLayout({
  sidebar,
  children,
  showSidebar = true,
  sidebarWidth = layout.sidebarWidth,
  ...headerProps
}: PortalLayoutProps) {
  const { t } = useTranslation('entity-portal');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <AppHeader scrollContainerRef={scrollContainerRef} {...headerProps} />

      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {showSidebar ? (
          <Box
            component="aside"
            aria-label={t('product.productMenuAria')}
            sx={{
              width: sidebarWidth,
              flexShrink: 0,
              bgcolor: layout.sidebarBackground,
              borderRight: '1px solid',
              borderColor: 'divider',
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'none'
                  : '2px 0 6px rgba(0, 0, 0, 0.08)',
              overflow: 'auto',
            }}
          >
            {sidebar}
          </Box>
        ) : null}

        <Box
          component="main"
          aria-label={t('product.productDomainAria')}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            bgcolor: 'background.default',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
